import { environment } from "@app/relay/relay-environment";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { FunnelScreen, PrimaryButton } from "../screen";

import type { waitlistScreen_ReferralApplyMutation } from "@app/queries/waitlistScreen_ReferralApplyMutation.graphql";
import type { Lead } from "../types";

const referralApplyMutation = graphql`
  mutation waitlistScreen_ReferralApplyMutation(
    $token: String
    $code: String!
  ) {
    leadReferralApply(token: $token, code: $code) {
      lead {
        position
        waitDays
      }
      errors {
        message
      }
    }
  }
`;

// The last screen. It says two things: where they are, and what moves them.
//
// What it does NOT say is why they are there. The queue is ranked by a fit score
// we keep to ourselves — it is an honest description of who we are built for and
// a rude thing to show someone about themselves — so this page talks about the
// two levers they actually hold: the data they hand over, and who they bring.
export default function WaitlistScreen({
  lead,
  token,
  reload,
}: {
  lead: Lead;
  token: string | null;
  reload: () => Promise<void>;
}) {
  const name = lead.firstName?.trim();

  return (
    <FunnelScreen
      title={name ? `You're on the list, ${name}` : "You're on the list"}
    >
      <div className="mt-6 grid grid-cols-2 gap-3">
        <Stat
          label="Your spot"
          value={lead.position ? `#${lead.position}` : "—"}
          hint="Live — it moves as the queue is worked"
        />
        <Stat
          label="Data completeness"
          value={`${lead.completenessScore}%`}
          hint="What we can see so far"
        />
      </div>

      <p className="mt-6 text-body text-zinc-600">
        Onboarding is concierge, so we work the list in the order we can rebuild
        a picture. The more of your books we can already read, the sooner we can
        get to yours — anything you add later moves you up.
      </p>

      {lead.waitDays != null && (
        <p className="mt-3 text-body text-zinc-500">
          At your current spot we&rsquo;d expect to reach you in around{" "}
          {lead.waitDays} days.
        </p>
      )}

      <Referral lead={lead} token={token} reload={reload} />
    </FunnelScreen>
  );
}

// The one lever that isn't waiting. Their own code moves them up every time
// somebody uses it, and entering someone else's is worth a one-off jump.
function Referral({
  lead,
  token,
  reload,
}: {
  lead: Lead;
  token: string | null;
  reload: () => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const submit = () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    commitMutation<waitlistScreen_ReferralApplyMutation>(environment, {
      mutation: referralApplyMutation,
      variables: { token, code: code.trim() },
      onCompleted: async (response) => {
        const message = response.leadReferralApply?.errors?.[0]?.message;
        setBusy(false);
        if (message) {
          setError(message);
          return;
        }
        setApplied(true);
        await reload();
      },
      onError: () => {
        setBusy(false);
        setError("Something went wrong. Please try again.");
      },
    });
  };

  return (
    <div className="mt-8 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
      <p className="text-caption text-zinc-700">Move up the list</p>

      {lead.referralCode && (
        <p className="mt-2 text-body text-zinc-600">
          Share your code{" "}
          <span className="font-mono font-semibold text-zinc-900">
            {lead.referralCode}
          </span>
          . Every person who joins with it moves you up.
        </p>
      )}

      {applied ? (
        <p className="mt-3 text-body text-emerald-700">
          Code applied — you&rsquo;ve moved up.
        </p>
      ) : (
        <form
          className="mt-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label htmlFor="referral-code" className="sr-only">
            Someone else&rsquo;s referral code
          </label>
          <input
            id="referral-code"
            name="referral-code"
            type="text"
            value={code}
            placeholder="Got a code? Enter it here"
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            className="h-11 w-full rounded-lg border border-zinc-300 px-3 font-mono text-body text-zinc-900 transition-colors placeholder:font-sans placeholder:text-zinc-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
          />
          {error && (
            <p className="mt-1.5 text-caption font-normal text-red-600">
              {error}
            </p>
          )}
          <div className="mt-3">
            <PrimaryButton type="submit" disabled={busy}>
              {busy ? "Checking…" : "Apply code"}
            </PrimaryButton>
          </div>
        </form>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
      <p className="text-caption text-zinc-700">{label}</p>
      <p className="mt-1 text-stat font-semibold tabular-nums text-zinc-900">
        {value}
      </p>
      <p className="mt-1 text-caption font-normal text-zinc-500">{hint}</p>
    </div>
  );
}
